// ============================================================
// Insurance Marketing Machine — industry vertical landing page
// Slingshot look & feel. Deploys to WP slug: insurance-marketing-machine
// ============================================================
import {
  Search,
  Sparkles,
  MousePointerClick,
  Repeat,
  Car,
  Home,
  Heart,
  Briefcase,
  HeartPulse,
  Umbrella,
  Gauge,
  Workflow,
  BarChart3,
} from "lucide-react";

export const INSURANCE_PAGE = {
  hero: {
    eyebrow: "Industry Vertical \u00b7 Insurance",
    titlePre: "The Insurance ",
    titleAccent: "Marketing Machine\u2122",
    sub: "Purpose-built visibility for agencies and carriers \u2014 own local search, get cited by AI engines, and capture rate shoppers the moment they're ready to switch.",
    bg: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1920&auto=format&fit=crop",
    ctaText: "Get My Free Insurance Visibility Audit",
  },
  pains: {
    heading: "Rate shoppers don't wait. Neither can your marketing.",
    sub: "Insurance buying has changed faster than most agency marketing has. Three shifts are quietly deciding who wins your market:",
    items: [
      {
        icon: Search,
        title: "The comparison-site squeeze",
        line: "Aggregators and comparison engines now outrank local agencies for the searches that used to be yours.",
      },
      {
        icon: Sparkles,
        title: "AI answers first",
        line: "ChatGPT and Google AI Overviews recommend agencies and carriers before a single click ever happens.",
      },
      {
        icon: Repeat,
        title: "The 90-day shopper",
        line: "Policyholders shop quietly before renewal. If you're invisible during that window, you never see the quote request.",
      },
    ],
  },
  machine: {
    eyebrow: "The machine, tuned for insurance",
    heading: "Four engines calibrated to how policies are actually bought.",
    sub: "The same Web Marketing Machine\u2122 that powers our most competitive clients \u2014 configured line-by-line for insurance.",
    items: [
      {
        icon: Search,
        title: "Foundational SEO\u2122 for Insurance",
        line: "Own \u201cinsurance agency near me\u201d and every line-of-business search in the cities you serve.",
      },
      {
        icon: Sparkles,
        title: "GEO for Insurance",
        line: "Be the agency AI engines cite when shoppers ask who to trust with their coverage.",
      },
      {
        icon: MousePointerClick,
        title: "Advanced PPC for Quotes",
        line: "High-intent quote keywords, bid to a target cost-per-bound-policy \u2014 not cost-per-click.",
      },
      {
        icon: Repeat,
        title: "Rate-Shopper Retargeting",
        line: "Stay in front of comparison shoppers through the entire renewal window.",
      },
    ],
  },
  lines: {
    eyebrow: "Every line of business",
    heading: "One machine. Every policy type.",
    items: [
      { icon: Car, name: "Auto", line: "Quote-intent capture in a comparison-heavy market." },
      { icon: Home, name: "Home", line: "Own the moments after every closing and claim." },
      { icon: Heart, name: "Life", line: "Long-cycle nurture for a considered decision." },
      { icon: Briefcase, name: "Commercial", line: "B2B pipelines for P&C, liability and workers' comp." },
      { icon: HeartPulse, name: "Health & Medicare", line: "Compliant enrollment-window campaigns." },
      { icon: Umbrella, name: "Specialty & Umbrella", line: "Niche coverage, found by the people who need it." },
    ],
  },
  process: {
    eyebrow: "How it works",
    heading: "From invisible to inevitable \u2014 in three moves.",
    steps: [
      {
        icon: Gauge,
        title: "Audit",
        body: "We map your visibility across search, AI answers, maps and paid media \u2014 against the agencies you actually compete with \u2014 and find the fastest wins.",
      },
      {
        icon: Workflow,
        title: "Build",
        body: "Your machine launches: SEO foundation, GEO entities, quote-intent PPC and renewal-window retargeting \u2014 live in weeks, not quarters.",
      },
      {
        icon: BarChart3,
        title: "Optimize",
        body: "The automation core shifts budget weekly toward whatever is binding policies cheapest \u2014 and reports one number that matters: revenue.",
      },
    ],
  },
  cta: {
    heading: "See what the Machine can do for your book.",
    sub: "Free Insurance Visibility Audit \u2014 your rankings, AI citations and paid efficiency versus the agencies you compete with. From a US-based strategist, in 2 business days or less.",
    primaryText: "Get My Free Insurance Visibility Audit",
    secondaryText: "Talk to a Strategist",
    secondaryHref: "https://www.slingshotseo.com/contact/",
  },
};

// ---------- Insurance FAQ (also powers FAQPage schema) ----------
export const INSURANCE_FAQ = {
  heading: "Insurance marketing questions, answered",
  items: [
    {
      q: "Do you work with independent agencies, captive agents or carriers?",
      a: "All three. The playbooks scale from single-office independents to multi-state carriers \u2014 strategy is calibrated to your book, your lines and your territory.",
    },
    {
      q: "Is your marketing compliant for the insurance industry?",
      a: "Yes. Claims-safe copy, required disclosures where applicable, and privacy-aware tracking \u2014 built for a regulated industry from day one.",
    },
    {
      q: "How do you compete with the comparison sites?",
      a: "We don't try to outspend the aggregators \u2014 we out-position them: local search dominance, AI-engine citations and renewal-window retargeting, exactly where comparison sites are weakest.",
    },
    {
      q: "What does cost-per-bound-policy bidding mean?",
      a: "We optimize paid campaigns toward the real business outcome \u2014 a bound policy \u2014 not clicks or raw leads, using conversion tracking tied to your quote flow.",
    },
    {
      q: "How fast can the Insurance Marketing Machine launch?",
      a: "Weeks, not quarters. The audit comes first, then SEO foundation, GEO entities, quote-intent PPC and retargeting go live in a staged rollout.",
    },
  ],
};
